/**
 * Created by jonas on 2021-02-09.
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {DDay} from "../DDay";
import {BlitzkriegSymbols, BlitzKriegSymbolState} from "./DDaySymbolConfig";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {IBaseSymbol} from "../../core/symbol/BaseSymbol";
import {Bounce, gsap, TimelineLite} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MultiSprites} from "../../core/utils/MultiSprite";
import {BlitzKriegGameMode} from "../server/BlitzKriegGameMode";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {DDayGameAssets} from "../DDayGameAssets";

export class DDaySymbol extends PIXI.Container implements IBaseSymbol {
    public symbolWidth: number = 144;
    public symbolHeight: number = 118;
    public bottomPadding: number = 0;
    public isSplit: boolean = false;
    public leftToRight: boolean = false;
    public symbolName: string;
    public stackedSymbolName: IParsedStackedSymbolName;
    public reelId: number;
    protected _brickImg: PIXI.Sprite;
    protected _animation: MultiSprites;
    protected _centerPosition: PIXI.Point;
    protected _text: BitmapCountUp;
    protected _multiplierText: number;
    protected _textInitialYValue: number;
    protected _wildWinAnimation: PIXI.spine.Spine | null;
    protected _wildIndex: number = 0;

    public get symbolStep(): number {
        return this.symbolHeight + this.bottomPadding;
    }

    constructor(symbolName: string, reelId: number, initState: BlitzKriegSymbolState) {
        super();
        this.symbolName = symbolName;
        this.stackedSymbolName = StackedSymbol.parseStackedSymbolName(symbolName, reelId);
        this.reelId = reelId;
        this.initAnimation();
        this.getStateAnimation(initState, {duration: 0, alpha: 1}, {duration: 0});
    }


    public set brickImg(brickImg: PIXI.Sprite) {
        this._brickImg = brickImg;
    }

    public get brickImg() {
        return this._brickImg;
    }

    public hideSpinSymbol(): void {
        if (this._animation.sprites[BlitzKriegSymbolState.SPIN]) {
            this._animation.sprites[BlitzKriegSymbolState.SPIN].visible = false;
        }
    }

    protected initAnimation(): void {
        this._centerPosition = this.getCenterPosition();
        this._animation = new MultiSprites();
        this._animation.position.set(this._centerPosition.x, this._centerPosition.y);
        this._multiplierText = 1;
        const style = {
            fontName: DDayGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 15,
        };
        this._text = new BitmapCountUp(style);
        this._text.text = "x" + this._multiplierText;
        this._text.anchor = new PIXI.Point(0.5, 0.5);
        this._text.position.set(27, -30);
        this._text.alpha = 0;
        this._textInitialYValue = this._text.y;
        this.addChild(this._animation, this._text);
    }

    protected getCenterPosition(): PIXI.Point {
        return new PIXI.Point(0, 0);
    }

    public getStateAnimation(state: BlitzKriegSymbolState, inVars: gsap.TweenVars, outVars: gsap.TweenVars, frames?: number[]): TimelineLite {
        // TODO: verify the state from symbol config?
        // TODO: change the animation to spine if the symbol is using spine instead of sprite.
        let _tl: TimelineLite = new TimelineLite();
        if (this.symbolName === BlitzkriegSymbols.S && state === BlitzKriegSymbolState.NO_WIN && DDay.model.parsedGameData.nextMode !== BlitzKriegGameMode.NORMAL) {
            state = BlitzKriegSymbolState.NORMAL;
        }
        if (this.isSplit && state !== BlitzKriegSymbolState.SPIN) {
            switch (state) {
                case BlitzKriegSymbolState.NORMAL:
                    state = this.leftToRight ? BlitzKriegSymbolState.SPLIT1_NORMAL : BlitzKriegSymbolState.SPLIT2_NORMAL;
                    break;
                case BlitzKriegSymbolState.WIN:
                    state = this.leftToRight ? BlitzKriegSymbolState.SPLIT1_WIN : BlitzKriegSymbolState.SPLIT2_WIN;
                    break;
                case BlitzKriegSymbolState.NO_WIN:
                    state = this.leftToRight ? BlitzKriegSymbolState.SPLIT1_NO_WIN : BlitzKriegSymbolState.SPLIT2_NO_WIN;
                    break;
            }
        }

        if (state === BlitzKriegSymbolState.NO_WIN) {
            _tl.add(() => {
                this._text.tint = 0x555555;
            }, 0);
        } else {
            _tl.add(() => {
                this._text.tint = 0xffffff;
            }, 0);
        }

        if (this.symbolName.includes(BlitzkriegSymbols.W) && state === BlitzKriegSymbolState.WIN) {
            let animName: string = this.getWildWinAnimationName(this._wildIndex);
            let _winTl: TimelineLite = new TimelineLite();
            _winTl.add([() => {
                if (this._wildWinAnimation) {
                    this.removeChild(this._wildWinAnimation);
                    this._wildWinAnimation = null;
                    this._animation.alpha = 1;
                }
                this._wildWinAnimation = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("Wild"));
                this.addChildAt(this._wildWinAnimation, 1);
                this._animation.alpha = 0;
                this._wildWinAnimation!.state.setAnimation(0, animName, false);
            }, _tl]);
            _winTl.to(this, {duration: 0.8});
            return _winTl;
        } else {
            if (this._wildWinAnimation) {
                this.removeChild(this._wildWinAnimation);
                this._wildWinAnimation = null;
                this._animation.alpha = 1;
            }
        }
        this.createState(state);
        if (this.symbolName.includes(BlitzkriegSymbols.W) && state === BlitzKriegSymbolState.SPIN) {
            this.alpha = 0.4;
        } else {
            this.alpha = 1;
        }
        _tl.add([this._animation.playSprite(state, inVars, outVars, frames), () => {
            if (this.reelId === 4 && this.symbolName === BlitzkriegSymbols.S && state === BlitzKriegSymbolState.WIN) {
                this.pivot.y = 75;
            } else {
                this.pivot.y = 0;
            }
        }], 0);
        return _tl;
    }

    protected createState(state: BlitzKriegSymbolState): void {
        if (!this._animation.sprites[state]) {
            this._animation.addSprites({
                name: state,
                textures: this.getStateTexture(state),
                anchor: new PIXI.Point(0.5, 0.5)
            });
        }
    }

    private getWildWinAnimationName(wildIndex: number) {
        let animName: string = "";
        switch (wildIndex) {
            case 0:
                animName = "baseWild_win";
                break;
            case 1:
                animName = "W1_win";
                break;
            case 2:
                animName = "W2_win";
                break;
            case 3:
                animName = "W3_win";
                break;
            case 4:
                animName = "W4_win";
                break;
            case 5:
                animName = "W5_win";
                break;
        }
        return animName;
    }

    public updateWildIndex(index: number) {
        this._wildIndex = index;
    }

    public changeSymbol(symbolName: string): TimelineLite {
        this.symbolName = symbolName;
        this.isSplit = false;
        for (let spritesKey in this._animation.sprites) {
            if (spritesKey == BlitzKriegSymbolState.NORMAL || spritesKey == BlitzKriegSymbolState.SPIN || spritesKey == BlitzKriegSymbolState.WIN) {
                let config = {
                    name: spritesKey,
                    textures: this.getStateTexture(spritesKey),
                    anchor: new PIXI.Point(0.5, 0.5)
                };
                let sprite = new TimelineSprite(config.textures);
                sprite.anchor.set(config.anchor.x, config.anchor.y);
                sprite.visible = false;
                this._animation.sprites[spritesKey] = sprite;
                this._animation.addChild(sprite);
            }
        }
        return this.getStateAnimation(BlitzKriegSymbolState.NORMAL, {duration: 0, alpha: 1}, {duration: 0});
    }

    public playWildMultiplierAnimation(): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            this._text.text = "x" + this._multiplierText;
        });
        if (this._text.alpha === 0) {
            let offset: number = this._text.y - 90;
            tl.fromTo(this._text, {
                y: offset,
                alpha: 0,
                duration: 0.1,
                delay: 0.05,
                ease: Bounce.easeIn
            }, {
                y: this._text.y,
                alpha: 1,
                ease: Bounce.easeOut
            });
        }
        return tl;
    }

    public showWildMultiplier(): void {
        this._text.text = "x" + this._multiplierText;
        this._text.alpha = 1;
    }

    protected getStateTexture(state?: BlitzKriegSymbolState | string): PIXI.Texture[] {
        let symbolName: string = this.symbolName;
        if (this.symbolName === BlitzkriegSymbols.S && this.reelId === 4) {
            symbolName = BlitzkriegSymbols.S2;
        }
        let texturePath: string = `${symbolName}/${symbolName}`;
        if (symbolName.includes(BlitzkriegSymbols.W)) {
            if (this._wildIndex > 0) {
                texturePath = `W1/W` + this._wildIndex;
                if (state === BlitzKriegSymbolState.NO_WIN) {
                    texturePath += `_${state}`;
                } else if (state === BlitzKriegSymbolState.SPIN) {
                    texturePath = `W1/W1` + `_${state}`;
                }
            } else {
                if (state === BlitzKriegSymbolState.SPIN || state === BlitzKriegSymbolState.NO_WIN) {
                    texturePath += `_${state}`;
                }
            }
        } else {
            if (state === BlitzKriegSymbolState.SPIN || state === BlitzKriegSymbolState.NO_WIN) {
                texturePath += `_${state}`;
            } else if (state === BlitzKriegSymbolState.WIN || state === BlitzKriegSymbolState.LAND || state === BlitzKriegSymbolState.GROOVE) {
                texturePath = `${symbolName}` + `_${state}` + `/${symbolName}` + `_${state}` + `_`;
            } else if (state === BlitzKriegSymbolState.SPLIT1_TRANSFORM || state === BlitzKriegSymbolState.SPLIT2_TRANSFORM) {
                texturePath = `Split_symbols/` + `${symbolName}` + `_split/` + `transform/` + `${state}` + `/${symbolName}` + `_${state}` + `_`;
            } else if (state === BlitzKriegSymbolState.SPLIT1_NORMAL || state === BlitzKriegSymbolState.SPLIT2_NORMAL) {
                texturePath = `Split_symbols/` + `${symbolName}` + `_split/` + `normal` + `/${symbolName}` + `_${state}`;
            } else if (state === BlitzKriegSymbolState.SPLIT1_NO_WIN || state === BlitzKriegSymbolState.SPLIT2_NO_WIN) {
                texturePath = `Split_symbols/` + `${symbolName}` + `_split/` + `noWin` + `/${symbolName}` + `_${state}`;
            } else if (state === BlitzKriegSymbolState.SPLIT1_WIN || state === BlitzKriegSymbolState.SPLIT2_WIN) {
                texturePath = `Split_symbols/` + `${symbolName}` + `_split/` + `win/` + `${state}` + `/${symbolName}` + `_${state}` + `_`;
            } else if (state === BlitzKriegSymbolState.STOPPER && !this.isSplit) {
                texturePath = `${symbolName}` + `_stopper` + `/${symbolName}` + `_${state}` + `_`;
            } else if (state === BlitzKriegSymbolState.STOPPER && this.isSplit) {
                texturePath = `Split_symbols/` + `${symbolName}` + `_split` + `/stopper/` + (this.leftToRight ? `stopper1` : `stopper2`) + `/${symbolName}_stopper_split` + (this.leftToRight ? `1` : `2`) + `_`;
            }
        }

        return DDay.assetLoader.getTextures(texturePath);
    }

    public resetMultiplierPosition(): void {
        if (this._text.alpha == 0) {
            this._text.y = this._textInitialYValue;
        }
    }

    public onRemove(): void {
        this.destroy({children: true});
    }

    public getGlobalPosition(): PIXI.Point {
        if (this.parent) {
            return new PIXI.Point(this.position.x + this.parent.x,
                this.position.y + this.parent.y);
        }
        return this.position;
    }

    //For future use to get symbol multiplier
    public getSymbolMultiplier(): number {
        return this._multiplierText;
    }

    public destroySymbol(): void {
        this._text.destroy();
        this._animation.destroy();
        this.destroy();
    }

    public setSymbolMultiplier(multiplier: number): void {
        this._multiplierText = multiplier;
    }
}
